import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ExpiredBankBanner — owner-only banner that appears at the top of the app
 * when one or more TrueLayer bank connections have been marked `expired`
 * by the keep-alive loop. Tapping it routes the user to Sync Centre so
 * they can re-auth via the bank's SCA flow.
 *
 * Why a banner (not a push notification): the keep-alive loop runs daily,
 * but owners don't always log in daily. A persistent banner inside the
 * app catches them the next time they DO open it — without needing to
 * configure native push permissions.
 *
 * Dismissal: storing dismissed=true in sessionStorage hides it for the
 * rest of the session (cleared on next browser open). We deliberately
 * don't persist to localStorage — if the user dismisses on Monday and the
 * bank is STILL expired on Wednesday, they should see it again. Better
 * a small annoyance than missing days of bank-reconciled payments.
 */
const ExpiredBankBanner = ({ user, setCurrentPage }) => {
  const [expiredBanks, setExpiredBanks] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  // Only owners see this — workers don't manage bank integrations.
  const isOwner = user && user.role !== 'Worker';

  useEffect(() => {
    if (!isOwner) return;
    if (sessionStorage.getItem('expiredBankBannerDismissed') === '1') {
      setDismissed(true);
      return;
    }

    const fetchExpired = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        if (!token) return;
        const res = await fetch(`${API_URL}/api/truelayer/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const expired = (data.banks || []).filter(b => b.status === 'expired');
        setExpiredBanks(expired);
      } catch (err) {
        // Network blips shouldn't surface a banner — silently skip.
        console.warn('ExpiredBankBanner fetch failed:', err);
      }
    };
    fetchExpired();
  }, [isOwner]);

  const handleDismiss = () => {
    sessionStorage.setItem('expiredBankBannerDismissed', '1');
    setDismissed(true);
  };

  const handleReconnect = () => {
    if (setCurrentPage) setCurrentPage('sync-centre');
  };

  if (!isOwner || dismissed || expiredBanks.length === 0) return null;

  const label = expiredBanks.length === 1
    ? `${expiredBanks[0].bank_name} bank connection expired`
    : `${expiredBanks.length} bank connections expired`;

  return (
    <div data-testid="expired-bank-banner">
      {/* Dark spacer fills the iPhone status-bar safe area so the banner
          content sits BELOW the clock/battery icons and stays tappable */}
      <div className="safe-area-top bg-zinc-900" aria-hidden="true" />
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
        <p className="text-sm text-amber-900 truncate">
          <span className="font-semibold">{label}</span>
          <span className="hidden sm:inline"> — payments won't auto-reconcile until reconnected.</span>
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleReconnect}
          className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
          data-testid="expired-bank-reconnect-btn"
        >
          Reconnect
        </button>
        <button
          onClick={handleDismiss}
          className="p-1.5 hover:bg-amber-100 rounded-lg transition-colors"
          aria-label="Dismiss"
          data-testid="expired-bank-dismiss-btn"
        >
          <X size={16} className="text-amber-700" />
        </button>
      </div>
      </div>
    </div>
  );
};

export default ExpiredBankBanner;
